import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, PieChart, Pie, Cell, LabelList } from "recharts";
import { AlertTriangle, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { get } from "@/lib/api";
import { ChartCard, ChartTooltip, GRID, CATEGORY_COLORS } from "@/lib/charts";
import { Skel } from "@/components/app/Common";

function Funnel({ data }) {
  const max = Math.max(1, data[0]?.count || 0);
  return (
    <div className="space-y-2" data-testid="funnel-chart">
      {data.map((s, i) => {
        const conv = i && data[i - 1].count ? Math.round((100 * s.count) / data[i - 1].count) : null;
        return (
          <div key={s.stage} className="flex items-center gap-3 text-sm">
            <span className="w-36 text-ink-secondary">{s.stage}</span>
            <div className="flex flex-1 justify-center"><div className="h-6 rounded-md bg-brand transition-all duration-200" style={{ width: `${Math.max(4, (100 * s.count) / max)}%`, opacity: 1 - i * 0.12 }} /></div>
            <span className="w-10 text-right font-medium tabular-nums">{s.count}</span>
            <span className="w-12 text-right text-xs text-ink-muted tabular-nums">{conv != null ? `${conv}%` : ""}</span>
          </div>
        );
      })}
    </div>
  );
}

export function AttentionList({ items }) {
  const nav = useNavigate();
  if (!items.length) return null;
  const go = (it) => nav(it.type === "shortlist" ? `/app/jobs/${it.job_id}/candidates` : it.type.startsWith("assessment") || it.type === "expiring_invites" ? "/app/assessments" : "/app/candidates");
  return (
    <section className="panel divide-y divide-line" data-tour="pt-attention-list" data-testid="attention-list">
      <h2 className="px-5 py-3 text-base font-semibold">Needs your attention</h2>
      {items.map((it, i) => (
        <div key={i} className="flex items-center gap-3 px-5 py-3" data-testid={`attention-item-${i}`}>
          <AlertTriangle size={16} className="text-caution" />
          <p className="flex-1 text-sm text-ink">{it.text}</p>
          <Button size="sm" variant="outline" onClick={() => go(it)} data-testid={`attention-action-${i}`}>{it.type === "shortlist" ? "Review and shortlist" : it.type === "assessment_review" ? "Review" : it.type === "expiring_invites" ? "View invites" : "Resolve"} <ArrowRight size={14} /></Button>
        </div>
      ))}
    </section>
  );
}

export function DashboardCharts() {
  const { data, isLoading } = useQuery({ queryKey: ["dashboard-analytics"], queryFn: () => get("/dashboard/analytics") });
  if (isLoading) return <div className="grid gap-6 lg:grid-cols-3">{[0, 1, 2].map((i) => <Skel key={i} className="h-72" />)}</div>;
  const total = data.categories.reduce((a, c) => a + c.value, 0);
  const strong = data.categories.find((c) => c.name === "Strong Fit")?.value || 0;
  const above = data.fit_histogram.filter((b) => b.start >= Math.floor(data.fit_threshold)).reduce((a, b) => a + b.count, 0);
  const f = data.funnel;
  return (
    <div className="space-y-6">
      <AttentionList items={data.attention} />
      <div className="grid gap-6 lg:grid-cols-3" data-tour="pt-dashboard-charts" data-testid="dashboard-charts">
        <ChartCard title="Hiring funnel" testId="chart-funnel" takeaway={f[1]?.count ? `${Math.round((100 * f[2].count) / f[1].count)}% of screened candidates have been shortlisted.` : "No candidates screened yet."}><Funnel data={f} /></ChartCard>
        <ChartCard title="Fit Score distribution" testId="chart-histogram" takeaway={data.fit_count ? `${above} of ${data.fit_count} candidates score around or above the Recommend line (${data.fit_threshold}).` : "Fit Scores appear once resumes are screened."}>
          <ResponsiveContainer width="100%" initialDimension={{ width: 320, height: 200 }} height={220}>
            <BarChart data={data.fit_histogram}>
              <CartesianGrid stroke={GRID} vertical={false} />
              <XAxis dataKey="start" tick={{ fontSize: 11, fill: "#8A8D85" }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#8A8D85" }} width={24} />
              <Tooltip content={<ChartTooltip />} cursor={{ fill: "#FAF8F4" }} />
              <ReferenceLine x={Math.floor(data.fit_threshold)} stroke="#9A6700" strokeDasharray="4 3" label={{ value: "Recommend", fontSize: 10, fill: "#9A6700", position: "top" }} />
              <Bar dataKey="count" name="Candidates" fill="#1F4D3A" radius={[6, 6, 0, 0]} isAnimationActive={false} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Categories" testId="chart-donut" takeaway={total ? `${Math.round((100 * strong) / total)}% of screened candidates are a Strong Fit.` : "No categories yet."}>
          <div className="relative">
            <ResponsiveContainer width="100%" initialDimension={{ width: 320, height: 200 }} height={220}>
              <PieChart>
                <Pie data={data.categories.filter((c) => c.value)} dataKey="value" nameKey="name" innerRadius={60} outerRadius={90} paddingAngle={2} isAnimationActive={false}>
                  {data.categories.filter((c) => c.value).map((c) => <Cell key={c.name} fill={CATEGORY_COLORS[c.name]} />)}
                  <LabelList dataKey="value" position="outside" fontSize={11} fill="#575B54" />
                </Pie>
                <Tooltip content={<ChartTooltip />} />
              </PieChart>
            </ResponsiveContainer>
            <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center"><span className="font-display text-2xl font-semibold" data-testid="donut-total">{total}</span><span className="text-xs text-ink-muted">screened</span></div>
          </div>
        </ChartCard>
      </div>
    </div>
  );
}
